import type { LucideIcon } from "lucide-react";
import type React from "react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface StatusBadgeProps {
	icon?: LucideIcon;
	text: React.ReactNode;
	color?: "blue" | "amber" | "green" | "rose" | "gray";
	tooltip?: string;
	onClick?: () => void;
	className?: string;
	iconClassName?: string;
	textClassName?: string;
}

export default function StatusBadge({
	icon: Icon,
	text,
	color = "gray",
	tooltip,
	onClick,
	className,
	iconClassName,
	textClassName,
}: StatusBadgeProps) {
	const colorClasses = {
		blue: "bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-800/30 text-blue-600 dark:text-blue-400",
		amber:
			"bg-amber-50 dark:bg-amber-900/30 border-amber-100 dark:border-amber-800/30 text-amber-600 dark:text-amber-400",
		green:
			"bg-green-50 dark:bg-green-900/30 border-green-100 dark:border-green-800/30 text-green-600 dark:text-green-400",
		rose: "bg-rose-50 dark:bg-rose-900/30 border-rose-100 dark:border-rose-800/30 text-rose-600 dark:text-rose-400",
		gray: "bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400",
	};

	const hoverClasses = onClick
		? "hover:shadow-sm hover:brightness-105 dark:hover:brightness-110 cursor-pointer transition-all"
		: "";

	const content = (
		<div
			className={cn(
				"inline-flex items-center rounded-full border px-2.5 py-1 font-medium text-xs",
				colorClasses[color],
				onClick ? hoverClasses : "",
				className,
			)}
			onClick={onClick}
			onKeyDown={
				onClick
					? (e) => {
							if (e.key === "Enter" || e.key === " ") onClick();
						}
					: undefined
			}
			role={onClick ? "button" : undefined}
			tabIndex={onClick ? 0 : undefined}
		>
			{Icon && <Icon className={cn("mr-1 h-3.5 w-3.5", iconClassName)} />}
			<span className={cn(textClassName)}>{text}</span>
		</div>
	);

	if (tooltip) {
		return (
			<TooltipProvider delayDuration={100}>
				<Tooltip>
					<TooltipTrigger asChild>{content}</TooltipTrigger>
					<TooltipContent>
						<p>{tooltip}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return content;
}
